import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircleHeart } from "lucide-react";

const SUNSET_HEART = "https://images.unsplash.com/photo-1518621736915-f3b1c41bfd00?auto=format&fit=crop&w=1200&q=80";

export const ShareStorySection = ({ title = "Share Your Story" }) => (
    <section className="bg-editorial py-20 md:py-28 overflow-hidden" data-testid="share-story-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="grid md:grid-cols-2 shadow-2xl"
            >
                {/* LEFT — Image */}
                <div className="relative overflow-hidden bg-coral aspect-[4/5] md:aspect-auto md:min-h-[520px]">
                    <motion.img
                        initial={{ scale: 1.1 }}
                        whileInView={{ scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 2, ease: "easeOut" }}
                        src={SUNSET_HEART}
                        alt="Silhouette of a person forming a heart shape with their hands against a sunset over water — representing love, hope, and shared healing"
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-navy-900/30" />
                    {/* Red vertical accent line */}
                    <div className="absolute top-0 bottom-0 right-0 w-px bg-coral" />
                </div>

                {/* RIGHT — Navy panel */}
                <div className="relative bg-navy-900 text-editorial p-10 md:p-14 lg:p-16 flex flex-col justify-center overflow-hidden">
                    {/* Subtle radial glow */}
                    <motion.div
                        className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-coral/15 blur-3xl"
                        animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.7, 0.4] }}
                        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <div className="relative">
                        <motion.h2
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-8 uppercase"
                        >
                            {title}
                        </motion.h2>
                        <p className="text-base md:text-lg text-editorial/85 leading-relaxed mb-6 max-w-md">
                            Have you or someone you love experienced a medical emergency in a hotel or public establishment?
                        </p>
                        <p className="font-serif italic text-xl md:text-2xl text-gold mb-6">
                            Your story matters.
                        </p>
                        <p className="text-base text-editorial/75 leading-relaxed mb-10 max-w-md">
                            By sharing experiences, we can continue raising awareness about the importance of CPR training, AED accessibility, and emergency preparedness.
                        </p>
                        <Link
                            to="/contact"
                            data-testid="share-story-cta"
                            className="group inline-flex items-center gap-3 px-7 py-4 bg-white text-navy-900 hover:bg-gold font-medium tracking-wide uppercase text-xs transition-colors duration-300"
                        >
                            <MessageCircleHeart className="w-4 h-4" />
                            Share Your Story
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>
                </div>
            </motion.div>
        </div>
    </section>
);

export default ShareStorySection;
